import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { GoogleMap, LoadScript, Marker, InfoWindow, Circle, MarkerClusterer, Polyline } from '@react-google-maps/api'
import { secureApi } from '../../Config/api'
import { socket } from '../../Config/socket'
import { GET } from '../../utils/apis'
import { mapBus } from "../../Assets/index"
import { useDispatch, useSelector } from 'react-redux'
import { verifyToken } from '../../utils/helpers'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { dateFormate, translations, tableDateFormate } from '../../utils/constants'
import { Select } from 'antd'
import moment from 'moment'
import { Datepicker } from '../../Components'

const LIVE_REFRESH_MS = 20000

const containerStyle = {
    width: '100%',
    height: 'calc(100vh - 71px)'
}

const { Option } = Select

const libraries = ['geometry']
const GOOGLE_MAPS_API_KEY = 'AIzaSyAOvlaMKD4C0dPSjCtNOi-DnwkW8s4pSbI'

const Maps = ({ language }) => {
    const mapRef = useRef(null)
    const history = useHistory()
    const dispatch = useDispatch()
    const isFirstLoad = useRef(true)

    const [boats, setBoats] = useState([])
    const user = useSelector((state) => state.authReducer?.user)
    const [selectedBoat, setSelectedBoat] = useState(null)
    const [mapCenter, setMapCenter] = useState({ lat: 45.4642, lng: 9.19 })
    const [mapZoom, setMapZoom] = useState(12)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedDate, setSelectedDate] = useState(new Date())
    const [selectedBoatName, setSelectedBoatName] = useState(null)

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [history, dispatch])

    const onMapLoad = useCallback((map) => {
        mapRef.current = map
    }, [])

    const onZoomChanged = useCallback(() => {
        if (mapRef.current) {
            setMapZoom(mapRef.current.getZoom())
        }
    }, [])

    useEffect(() => {
        fetchMapData()
    }, [selectedDate])

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371e3;
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    const fetchMapData = useCallback(async (silent = false) => {
        if (!user?.id) return
        try {
            if (!silent) setIsLoading(true)
            const formattedDate = moment(selectedDate).isValid() ? moment(selectedDate).format(tableDateFormate) : null
            const response = await secureApi.get(`${GET?.GET_ALL_LOCATION}?id=${user?.id}&formattedDate=${formattedDate}`)
            if (response?.success) {
                setBoats(response?.data)
                if (isFirstLoad.current && response?.data?.length > 0) {
                    const firstBoat = response.data[0]
                    setMapCenter({
                        lat: parseFloat(firstBoat.center_lat),
                        lng: parseFloat(firstBoat.center_lng)
                    })
                    isFirstLoad.current = false
                }
            }
        } catch (err) {
            console.error('Error fetching boat data:', err)
        } finally {
            if (!silent) setIsLoading(false)
        }
    }, [user?.id, selectedDate])

    // Live positions (declared after fetchMapData: the deps array is read at
    // render time, before a later const would be initialised): refresh (without the loading overlay) when the server
    // reports a change, and poll while showing today. The polling matters
    // because the simulation / router polls may run on another backend whose
    // socket events never reach this client.
    useEffect(() => {
        const refresh = () => fetchMapData(true)
        socket.on('boatTrackDevicesInfoForMap:changed', refresh)
        const isToday = moment(selectedDate).isSame(moment(), 'day')
        const timer = isToday ? setInterval(refresh, LIVE_REFRESH_MS) : null
        return () => {
            socket.off('boatTrackDevicesInfoForMap:changed', refresh)
            if (timer) clearInterval(timer)
        }
    }, [fetchMapData, selectedDate])

    const markersMemo = useMemo(() => {
        return (
            <MarkerClusterer
                options={{
                    gridSize: 60,
                    minimumClusterSize: 2,
                    styles: [{
                        url: mapBus,
                        height: 70,
                        width: 60,
                        textColor: "#ea050cff",
                        textSize: 30,
                        anchorText: [25, 35]
                    }]
                }}
            >
                {(clusterer) => (
                    <>
                        {boats?.map((boat) => {
                            const centerLat = parseFloat(boat.center_lat)
                            const centerLng = parseFloat(boat.center_lng)
                            const liveLat = boat.liveLocation ? parseFloat(boat.liveLocation.latitude) : centerLat
                            const liveLng = boat.liveLocation ? parseFloat(boat.liveLocation.longitude) : centerLng

                            const distance = calculateDistance(centerLat, centerLng, liveLat, liveLng)
                            const inRange = distance <= (parseFloat(boat.radius) || 1000)

                            const centerPos = { lat: centerLat, lng: centerLng }
                            const livePos = { lat: liveLat, lng: liveLng }

                            const iconSize = Math.round(35 * Math.pow(0.95, 12 - mapZoom))

                            return (
                                <React.Fragment key={boat.id}>
                                    <Circle
                                        center={centerPos}
                                        radius={parseFloat(boat.radius) || 1000}
                                        options={{
                                            fillColor: inRange ? '#90EE90' : '#FF0000',
                                            fillOpacity: 0.2,
                                            strokeColor: inRange ? '#228B22' : '#B22222',
                                            strokeWeight: 1,
                                            clickable: false
                                        }}
                                    />
                                    {boat.liveLocation && (
                                        <Polyline
                                            path={[centerPos, livePos]}
                                            options={{
                                                strokeColor: inRange ? '#008000' : '#FF0000',
                                                strokeWeight: 3,
                                                strokeOpacity: 0.8
                                            }}
                                        />
                                    )}
                                    <Marker
                                        position={livePos}
                                        clusterer={clusterer}
                                        icon={{
                                            url: boat.logo_base64 || mapBus,
                                            scaledSize: { width: iconSize, height: iconSize },
                                            anchor: { x: iconSize / 2, y: iconSize / 2 }
                                        }}
                                        onClick={() => setSelectedBoat(boat)}
                                    />
                                </React.Fragment>
                            )
                        })}
                    </>
                )}
            </MarkerClusterer>
        )
    }, [boats, mapZoom])

    return (
        <>
            <div className="map-date-picker-div">
                <div className="map-date-picker-inner-div">
                    <Select
                        className="map-vehicle-select"
                        showSearch
                        placeholder={translations[language]?.selectBoatName}
                        optionFilterProp="children"
                        onChange={(val) => {
                            const v = boats.find((boat) => boat.id === val)
                            if (!v) return
                            setSelectedBoatName(v.id);
                            setSelectedBoat(v)

                            const targetLat = v.liveLocation ? parseFloat(v.liveLocation.latitude) : parseFloat(v.center_lat);
                            const targetLng = v.liveLocation ? parseFloat(v.liveLocation.longitude) : parseFloat(v.center_lng);

                            mapRef.current?.panTo({ lat: targetLat, lng: targetLng });
                            setMapZoom(15);
                        }}
                        value={selectedBoatName || undefined}
                        style={{ width: '100%' }}
                    >
                        {boats?.map((v) => (
                            <Option key={v?.id} value={v?.id}>
                                {v?.boat_name}
                            </Option>
                        ))}
                    </Select>

                    <Datepicker
                        format={dateFormate}
                        value={selectedDate}
                        language={language}
                        onChange={(d) => setSelectedDate(d)}
                    />
                </div>
            </div>
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={mapZoom}
                        onLoad={onMapLoad}
                        onZoomChanged={onZoomChanged}
                        options={{ gestureHandling: 'greedy', disableDefaultUI: true }}
                    >
                        {markersMemo}

                        {selectedBoat && (
                            <InfoWindow
                                position={selectedBoat.liveLocation
                                    ? { lat: parseFloat(selectedBoat.liveLocation.latitude), lng: parseFloat(selectedBoat.liveLocation.longitude) }
                                    : { lat: parseFloat(selectedBoat.center_lat), lng: parseFloat(selectedBoat.center_lng) }
                                }
                                onCloseClick={() => {
                                    setSelectedBoat(null);
                                    setSelectedBoatName(null);
                                }}
                            >
                                <div style={{ color: '#000' }}>
                                    <div><strong>{translations[language]?.boatName}: </strong>{selectedBoat?.boat_name}</div>
                                    <div><strong>SN: </strong>{selectedBoat?.serial_number}</div>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>

                    {isLoading && (
                        <div style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            zIndex: 9999,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '1.5em',
                            color: '#333',
                            backgroundColor: 'rgb(0 0 0 / 30%)',
                        }}>
                            <div className="spinner-border" role="status">
                                <div className="dot-spinner">
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                    <div className="dot-spinner__dot"></div>
                                </div>
                                <span className="sr-only">{translations[language]?.loadingMap}</span>
                            </div>
                        </div>
                    )}
                </div>
            </LoadScript>
        </>
    )
}

export default Maps;
